import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAppStore } from '../store/useAppStore';
import { qk } from '../services/queryKeys';
import {
  fetchEmployees,
  fetchPerformanceCycles,
  fetchPerformanceGoals,
  createPerformanceGoalApi,
  updatePerformanceGoalApi,
  deletePerformanceGoalApi,
} from '../services/api';
import type { PerformanceGoal, PerformanceGoalStatus } from '../types';
import Chip, { type ChipVariant } from '../components/Chip';
import ProgressBar from '../components/ProgressBar';
import Button from '../components/Button';
import Modal from '../components/Modal';
import Empty from '../components/Empty';
import Alert from '../components/Alert';
import Icon from '../components/Icon';

const STATUS_VARIANT: Record<PerformanceGoalStatus, ChipVariant> = {
  completed: 'success', on_track: 'info', at_risk: 'warn', not_started: 'neutral',
};

const STATUS_OPTIONS: PerformanceGoalStatus[] = ['not_started', 'on_track', 'at_risk', 'completed'];

const label = (s: string) => s.replace(/_/g, ' ');

/** A goal may be deleted only before it has meaningfully progressed — the API enforces this too. */
const isDeletable = (g: PerformanceGoal) =>
  g.progressPercent === 0 && g.status === 'not_started';

export default function Performance() {
  const {
    role, profileId, employeeId, modal, openModal, closeModal, toast,
  } = useAppStore();
  // `employeeId` is resolved from GET /employees/me (see WebShell); `profileId`
  // is only a login-payload fallback.
  const myId = employeeId || profileId;
  const queryClient = useQueryClient();

  // Whose goals are on screen. ess never sends this — the server scopes it.
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const viewingEmployeeId = role === 'ess' ? undefined : selectedEmployeeId || undefined;

  const [form, setForm] = useState({ title: '', description: '', weight: '' });

  const {
    data: cycles = [], isLoading: cyclesLoading, isError: cyclesError,
  } = useQuery({
    queryKey: qk.performanceCycles(),
    queryFn: fetchPerformanceCycles,
  });

  const activeCycle = cycles.find((c) => c.status === 'active');

  const {
    data: goalsData, isLoading: goalsLoading, isError: goalsError,
  } = useQuery({
    queryKey: qk.performanceGoals(activeCycle?.id ?? '', viewingEmployeeId),
    queryFn: () => fetchPerformanceGoals(activeCycle!.id, viewingEmployeeId),
    enabled: !!activeCycle?.id,
  });

  // mss/hr pick whose goals to read or write. ess has no picker at all.
  const { data: employees = [] } = useQuery({
    queryKey: qk.employees(),
    queryFn: fetchEmployees,
    enabled: role !== 'ess',
  });

  const goals = goalsData?.goals ?? [];
  const invalidateGoals = () =>
    queryClient.invalidateQueries({ queryKey: ['performanceGoals'] });

  const createMutation = useMutation({
    mutationFn: (input: { employeeId: string; title: string; description?: string; weight: number }) =>
      createPerformanceGoalApi(activeCycle!.id, input),
    onSuccess: () => {
      invalidateGoals();
      toast('Goal added');
      setForm({ title: '', description: '', weight: '' });
      closeModal();
    },
    onError: (e: Error) => toast(e.message || 'Failed to add goal', 'danger'),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, ...input }: { id: string; progressPercent?: number; status?: PerformanceGoalStatus }) =>
      updatePerformanceGoalApi(id, input),
    onSuccess: () => invalidateGoals(),
    onError: (e: Error) => toast(e.message || 'Failed to update goal', 'danger'),
  });

  const deleteMutation = useMutation({
    mutationFn: deletePerformanceGoalApi,
    onSuccess: () => {
      invalidateGoals();
      toast('Goal removed');
    },
    onError: (e: Error) => toast(e.message || 'Failed to remove goal', 'danger'),
  });

  // The employee a new goal would be created for: ess always themselves,
  // mss/hr whoever is selected.
  const goalTargetId = role === 'ess' ? myId : selectedEmployeeId;

  // totalWeight is null for a multi-employee list, where a sum has no meaning.
  const totalWeight = goalsData?.totalWeight ?? null;
  const committedProgress = goals.reduce(
    (s, g) => s + g.progressPercent * (g.weight / 100),
    0,
  );
  // Deliberately weighted against 100, not against the weight actually
  // assigned: a 70%-complete goal worth 20 points is 14% of the plan, and
  // showing it as 70% would read as nearly-finished work.
  const overallProgress = Math.round(committedProgress);

  if (cyclesLoading) {
    return (
      <div>
        <div className="sk-page-header"><h1>Performance</h1></div>
        <div className="t-card"><div className="card-body">Loading…</div></div>
      </div>
    );
  }

  if (cyclesError) {
    return (
      <div>
        <div className="sk-page-header"><h1>Performance</h1></div>
        <Alert variant="danger">Could not load performance cycles. Please retry.</Alert>
      </div>
    );
  }

  return (
    <div>
      <div className="sk-page-header">
        <h1>Performance</h1>
        {activeCycle && (
          <Button
            variant="accent"
            disabled={!goalTargetId}
            onClick={() => openModal('new_goal')}
          >
            <Icon name="plus" size={14} color="white" />
            New goal
          </Button>
        )}
      </div>

      {role !== 'ess' && activeCycle && (
        <div className="t-card" style={{ marginBottom: 16 }}>
          <div className="card-body" style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <label htmlFor="perf-emp" style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)' }}>
              Employee
            </label>
            <select
              id="perf-emp"
              className="t-input"
              value={selectedEmployeeId}
              onChange={(e) => setSelectedEmployeeId(e.target.value)}
              style={{ maxWidth: 320 }}
            >
              <option value="">
                {role === 'mss' ? 'My team' : 'Everyone'}
              </option>
              {employees.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.firstName} {e.lastName ?? ''}
                </option>
              ))}
            </select>
            <span style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>
              {role === 'mss'
                ? 'Managers can open their direct reports.'
                : 'HR can open anyone in the organisation.'}
            </span>
          </div>
        </div>
      )}

      {!activeCycle ? (
        <div className="t-card">
          <Empty
            title="No active performance cycle"
            description="Goals appear here once HR activates a cycle."
            icon="star"
          />
        </div>
      ) : (
        <>
          <div className="t-card" style={{ marginBottom: 20 }}>
            <div className="card-body">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                <div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                    <h3 style={{ fontSize: 'var(--fs-lg)', fontWeight: 600 }}>{activeCycle.name}</h3>
                    <Chip variant="warn">in progress</Chip>
                  </div>
                  <p style={{ color: 'var(--ink-3)', fontSize: 'var(--fs-sm)' }}>
                    {activeCycle.startDate && activeCycle.endDate
                      ? `${activeCycle.startDate} – ${activeCycle.endDate}`
                      : activeCycle.period ?? 'Dates not set'}
                  </p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 32, fontWeight: 700, color: 'var(--accent)' }}>{overallProgress}%</div>
                  <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>Overall progress</div>
                </div>
              </div>
              <ProgressBar
                value={overallProgress}
                max={100}
                variant={overallProgress >= 70 ? 'success' : overallProgress >= 40 ? 'accent' : 'warn'}
              />
              {totalWeight !== null && (
                <p style={{ marginTop: 8, fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>
                  {totalWeight}% of 100% weight assigned
                  {totalWeight < 100 && ' — the plan is not fully weighted yet'}
                </p>
              )}
            </div>
          </div>

          {goalsError ? (
            <Alert variant="danger">Could not load goals for this cycle.</Alert>
          ) : goalsLoading ? (
            <div className="t-card"><div className="card-body">Loading goals…</div></div>
          ) : goals.length === 0 ? (
            <div className="t-card">
              <Empty
                title="No goals set for this cycle"
                description={
                  role === 'ess'
                    ? 'Add your first goal to start tracking progress.'
                    : 'Pick an employee, then add a goal for them.'
                }
                icon="star"
              />
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {goals.map((g) => (
                <div key={g.id} className="t-card">
                  <div className="card-body">
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 12 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <h3 style={{ fontSize: 'var(--fs-base)', fontWeight: 600 }}>{g.title}</h3>
                          <Chip variant={STATUS_VARIANT[g.status]}>{label(g.status)}</Chip>
                          {role !== 'ess' && g.employee && (
                            <Chip variant="neutral">
                              {g.employee.firstName} {g.employee.lastName ?? ''}
                            </Chip>
                          )}
                        </div>
                        {g.description && (
                          <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)' }}>{g.description}</p>
                        )}
                        {g.dueDate && (
                          <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)', marginTop: 4 }}>
                            due {g.dueDate}
                          </p>
                        )}
                      </div>
                      <div style={{ textAlign: 'right', flexShrink: 0 }}>
                        <div style={{ fontSize: 'var(--fs-xl)', fontWeight: 700, color: 'var(--ink)' }}>{g.progressPercent}%</div>
                        <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--ink-3)' }}>weight: {g.weight}%</div>
                      </div>
                    </div>

                    <ProgressBar
                      value={g.progressPercent}
                      max={100}
                      variant={g.status === 'completed' ? 'success' : g.status === 'at_risk' ? 'warn' : 'accent'}
                    />

                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 12, flexWrap: 'wrap' }}>
                      <input
                        type="range"
                        min={0}
                        max={100}
                        step={5}
                        value={g.progressPercent}
                        aria-label={`Progress for ${g.title}`}
                        disabled={updateMutation.isPending}
                        // Commit on release, not on every pixel of the drag.
                        onChange={() => {}}
                        onMouseUp={(e) =>
                          updateMutation.mutate({
                            id: g.id,
                            progressPercent: Number((e.target as HTMLInputElement).value),
                          })
                        }
                        onTouchEnd={(e) =>
                          updateMutation.mutate({
                            id: g.id,
                            progressPercent: Number((e.target as HTMLInputElement).value),
                          })
                        }
                        style={{ flex: 1, minWidth: 160 }}
                      />
                      <select
                        className="t-input"
                        value={g.status}
                        aria-label={`Status for ${g.title}`}
                        disabled={updateMutation.isPending}
                        onChange={(e) =>
                          updateMutation.mutate({
                            id: g.id,
                            status: e.target.value as PerformanceGoalStatus,
                          })
                        }
                        style={{ maxWidth: 160 }}
                      >
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s} value={s}>{label(s)}</option>
                        ))}
                      </select>
                      {isDeletable(g) && (
                        <Button
                          variant="danger"
                          size="sm"
                          disabled={deleteMutation.isPending}
                          onClick={() => deleteMutation.mutate(g.id)}
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* New goal modal */}
      {modal.type === 'new_goal' && activeCycle && (
        <Modal
          title="New goal"
          onClose={closeModal}
          footer={
            <>
              <Button variant="ghost" onClick={closeModal}>Cancel</Button>
              <Button
                variant="accent"
                disabled={createMutation.isPending}
                onClick={() => {
                  const weight = Number(form.weight);
                  if (!form.title.trim() || !form.weight) {
                    toast('Title and weight are required', 'warn'); return;
                  }
                  if (!Number.isInteger(weight) || weight < 0 || weight > 100) {
                    toast('Weight must be a whole number from 0 to 100', 'warn'); return;
                  }
                  if (!goalTargetId) {
                    toast('Pick an employee first', 'warn'); return;
                  }
                  createMutation.mutate({
                    employeeId: goalTargetId,
                    title: form.title.trim(),
                    description: form.description.trim() || undefined,
                    weight,
                  });
                }}
              >
                Add goal
              </Button>
            </>
          }
        >
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {role !== 'ess' && (
              <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)' }}>
                For: <strong style={{ color: 'var(--ink)' }}>
                  {employees.find((e) => e.id === goalTargetId)
                    ? `${employees.find((e) => e.id === goalTargetId)!.firstName} ${employees.find((e) => e.id === goalTargetId)!.lastName ?? ''}`
                    : 'select an employee above'}
                </strong>
              </p>
            )}
            <label>
              <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)' }}>Title</span>
              <input
                className="t-input"
                value={form.title}
                maxLength={200}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
            </label>
            <label>
              <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)' }}>Description</span>
              <textarea
                className="t-input"
                rows={3}
                value={form.description}
                maxLength={2000}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
            </label>
            <label>
              <span style={{ fontSize: 'var(--fs-sm)', color: 'var(--ink-3)' }}>
                Weight (%)
                {totalWeight !== null && totalWeight < 100 && ` — ${100 - totalWeight}% still unassigned`}
              </span>
              <input
                className="t-input"
                type="number"
                min={0}
                max={100}
                value={form.weight}
                onChange={(e) => setForm({ ...form, weight: e.target.value })}
              />
            </label>
          </div>
        </Modal>
      )}
    </div>
  );
}
